import { useState, useEffect } from 'react';
import { useCID10Search } from '@/hooks';

interface CID10AutocompleteProps {
  value: string;
  onChange: (code: string, description: string) => void;
  onSelect?: (code: string, description: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function CID10Autocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Buscar código CID-10...',
  disabled = false,
}: CID10AutocompleteProps) {
  const [inputValue, setInputValue] = useState(value);
  const [showDropdown, setShowDropdown] = useState(false);

  const { data: results, isLoading } = useCID10Search(inputValue);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue, '');
    setShowDropdown(newValue.length >= 3);
  };

  const handleSelect = (code: string, description: string) => {
    setInputValue(code);
    onChange(code, description);
    if (onSelect) {
      onSelect(code, description);
    }
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      />

      {showDropdown && inputValue.length >= 3 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500">Buscando...</div>
          ) : results && results.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {results.map((item) => (
                <li
                  key={item.code}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleSelect(item.code, item.description)}
                >
                  <div className="flex items-start">
                    <span className="font-mono font-medium text-primary-600 mr-2">
                      {item.code}
                    </span>
                    <span className="text-sm text-gray-700">{item.description}</span>
                  </div>
                  <span className="text-xs text-gray-400 mt-1 block">
                    {item.category}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500">
              Nenhum resultado encontrado
            </div>
          )}
        </div>
      )}
    </div>
  );
}
